import { useState, useEffect, useCallback } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tag, Plus, Edit2, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function CarwashServicios() {
  const [servicios, setServicios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre: '', descripcion: '', precio: '', duracion_min: '30' });
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/carwash/servicios');
      setServicios(res.data);
    } catch { toast.error('Error al cargar servicios'); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({ nombre: '', descripcion: '', precio: '', duracion_min: '30' });
    setDialog(true);
  };

  const abrirEditar = (s) => {
    setEditando(s);
    setForm({ nombre: s.nombre, descripcion: s.descripcion || '', precio: String(s.precio), duracion_min: String(s.duracion_min || 30) });
    setDialog(true);
  };

  const guardar = async () => {
    if (!form.nombre) { toast.error('El nombre es obligatorio'); return; }
    if (!form.precio || parseFloat(form.precio) <= 0) { toast.error('El precio debe ser mayor a 0'); return; }
    setGuardando(true);
    try {
      if (editando) {
        await api.put(`/carwash/servicios/${editando.id}`, form);
        toast.success('Servicio actualizado');
      } else {
        await api.post('/carwash/servicios', form);
        toast.success('Servicio creado');
      }
      setDialog(false);
      cargar();
    } catch { toast.error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Eliminar este servicio?')) return;
    try {
      await api.delete(`/carwash/servicios/${id}`);
      toast.success('Servicio eliminado');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-servicios-page">
      <div className="flex items-center justify-between">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Tag className="w-8 h-8 text-cyan-400" /> Servicios de Lavado
        </h1>
        <Button onClick={abrirNuevo} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="nuevo-servicio-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Servicio
        </Button>
      </div>

      {cargando ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-slate-800 rounded-sm animate-pulse" />)}
        </div>
      ) : servicios.length === 0 ? (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-12 text-center">
            <Tag className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500">No hay servicios configurados</p>
            <p className="text-xs text-slate-600 mt-1">Agrega tu primer servicio de lavado</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {servicios.map(s => (
            <Card key={s.id} className={`border-slate-800 ${s.activo !== false ? 'bg-slate-900/40' : 'bg-slate-900/20 opacity-60'}`} data-testid={`servicio-card-${s.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-100 font-barlow">{s.nombre}</h3>
                    {s.descripcion && <p className="text-xs text-slate-500 mt-1">{s.descripcion}</p>}
                  </div>
                  {s.activo === false && <Badge className="bg-red-500/20 text-red-400 border-red-800 text-[10px]">Inactivo</Badge>}
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold text-cyan-400 font-mono">{formatoMXN(s.precio)}</p>
                    <div className="flex items-center gap-1 mt-1 text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span className="text-xs">{s.duracion_min} min</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => abrirEditar(s)} className="text-slate-400 hover:text-cyan-400 h-8 w-8" data-testid={`editar-servicio-${s.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => eliminar(s.id)} className="text-slate-400 hover:text-red-400 h-8 w-8" data-testid={`eliminar-servicio-${s.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="servicio-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">{editando ? 'Editar Servicio' : 'Nuevo Servicio'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
              <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Lavado Basico" className="bg-slate-950 border-slate-700 text-slate-200" data-testid="servicio-nombre-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Descripcion</Label>
              <Textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder="Que incluye el servicio" className="bg-slate-950 border-slate-700 text-slate-200" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Precio (MXN) *</Label>
                <Input type="number" step="0.01" value={form.precio} onChange={e => setForm({ ...form, precio: e.target.value })} placeholder="0.00" className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="servicio-precio-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Duracion (min)</Label>
                <Input type="number" value={form.duracion_min} onChange={e => setForm({ ...form, duracion_min: e.target.value })} className="bg-slate-950 border-slate-700 text-slate-200 font-mono" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardar} disabled={guardando} className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold" data-testid="guardar-servicio-btn">
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
